import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ThemeToggle } from "../sirius/ThemeProvider";
import { LanguageToggle, useT } from "../i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** /reset-password?token=… — second leg of the password-reset flow.
 *  Verifies the token on mount (so we can show "invalid/expired" before the
 *  user types a password), then on submit POSTs the new password. After
 *  success, every existing JWT for the user is invalidated server-side,
 *  so we redirect to /login for a fresh sign-in. */
export default function ResetPassword() {
  const { lang } = useT();
  const isEn = lang === "en";
  const navigate = useNavigate();
  const tokenFromUrl = new URLSearchParams(window.location.search).get("token") || "";

  const [tokenState, setTokenState] = useState(tokenFromUrl ? "checking" : "invalid");
  const [emailMasked, setEmailMasked] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(tokenFromUrl ? "" : (isEn ? "Missing reset token in URL." : "Mangler tilbakestillings-token i lenken."));
  const [done, setDone] = useState(false);

  // Verify the token on mount (lightweight check, doesn't consume it)
  useEffect(() => {
    if (!tokenFromUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/auth/password-reset/verify?token=${encodeURIComponent(tokenFromUrl)}`);
        const d = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (!r.ok || !d.valid) {
          setTokenState("invalid");
          setError(d.reason === "expired"
            ? (isEn ? "This reset link has expired. Please request a new one." : "Denne lenken er utløpt. Vennligst be om en ny.")
            : (isEn ? "This reset link is invalid or already used." : "Denne lenken er ugyldig eller allerede brukt."));
          return;
        }
        setEmailMasked(d.email_masked || "");
        setTokenState("ready");
      } catch (e) {
        if (cancelled) return;
        setTokenState("invalid");
        setError(isEn ? "Could not verify reset link." : "Kunne ikke verifisere lenken.");
      }
    })();
    return () => { cancelled = true; };
  }, [tokenFromUrl, isEn]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    if (newPassword.length < 8) {
      setError(isEn ? "Password must be at least 8 characters." : "Passordet må ha minst 8 tegn.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(isEn ? "Passwords don't match." : "Passordene er ikke like.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/auth/password-reset/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: tokenFromUrl, new_password: newPassword }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : (isEn ? "Could not reset password" : "Kunne ikke tilbakestille passord"));
      setDone(true);
      // Bounce to /login after a short success screen — every existing JWT
      // for this user has been invalidated server-side, so they MUST log in
      // again with the new password.
      setTimeout(() => navigate("/login?reset=1", { replace: true }), 1800);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="srs" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }} data-testid="reset-password-root">
      <nav style={{ padding: "16px 28px", borderBottom: "1px solid var(--srs-line)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" style={{ color: "var(--srs-ink)", textDecoration: "none", fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 22 }}>EuroPilot</Link>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}><LanguageToggle /><ThemeToggle /></div>
      </nav>

      <main style={{ flex: 1, display: "grid", placeItems: "center", padding: 32 }}>
        {tokenState === "checking" && (
          <div className="srs-mono" style={{ letterSpacing: "0.3em", color: "var(--srs-ink-dim)", fontSize: 12 }} data-testid="reset-checking">
            {isEn ? "VERIFYING LINK…" : "VERIFISERER LENKE…"}
          </div>
        )}

        {tokenState === "invalid" && (
          <div className="srs-card" style={{ width: "100%", maxWidth: 460, padding: 40, borderRadius: 24 }} data-testid="reset-invalid">
            <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-warn)", marginBottom: 14 }}>× {isEn ? "LINK PROBLEM" : "LENKE-PROBLEM"}</div>
            <h1 className="srs-display" style={{ fontSize: 28, fontWeight: 500, lineHeight: 1.15, margin: "0 0 12px 0" }}>
              {isEn ? "Can't use this link" : "Kan ikke bruke denne lenken"}
            </h1>
            <p style={{ fontSize: 14, color: "var(--srs-ink-dim)", lineHeight: 1.55, margin: "0 0 24px 0" }}>{error}</p>
            <Link to="/forgot-password" className="srs-cta" style={{ display: "inline-block", textAlign: "center" }} data-testid="reset-request-new">
              {isEn ? "Request a new link →" : "Be om ny lenke →"}
            </Link>
          </div>
        )}

        {tokenState === "ready" && !done && (
          <form onSubmit={onSubmit} className="srs-card" style={{ width: "100%", maxWidth: 420, padding: 40, borderRadius: 24 }} data-testid="reset-form">
            <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-sun)", marginBottom: 14 }}>// {isEn ? "NEW PASSWORD" : "NYTT PASSORD"}</div>
            <h1 className="srs-display" style={{ fontSize: 30, fontWeight: 500, letterSpacing: "-0.02em", margin: "0 0 14px 0", lineHeight: 1.1 }}>
              {isEn ? "Set a new password" : "Sett et nytt passord"}
            </h1>
            {emailMasked && (
              <p style={{ fontSize: 13, color: "var(--srs-ink-dim)", margin: "0 0 22px 0" }}>
                {isEn ? "For account " : "For kontoen "}<strong style={{ color: "var(--srs-ink)" }}>{emailMasked}</strong>
              </p>
            )}
            <label style={labelCss}>{isEn ? "NEW PASSWORD" : "NYTT PASSORD"}</label>
            <input
              type="password"
              required
              autoFocus
              autoComplete="new-password"
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
              style={inputCss}
              data-testid="reset-new-password"
            />
            <label style={{ ...labelCss, marginTop: 16 }}>{isEn ? "CONFIRM PASSWORD" : "BEKREFT PASSORD"}</label>
            <input
              type="password"
              required
              autoComplete="new-password"
              minLength={8}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              style={inputCss}
              data-testid="reset-confirm-password"
            />
            <p style={{ fontSize: 11, color: "var(--srs-ink-faint)", margin: "10px 0 0 0", lineHeight: 1.5 }}>
              {isEn ? "Minimum 8 characters. After resetting, you'll be logged out of all other sessions." : "Minst 8 tegn. Etter tilbakestilling logges du ut av alle andre økter."}
            </p>
            {error && <div style={errorCss} data-testid="reset-error">{error}</div>}
            <button type="submit" disabled={submitting || !newPassword || !confirmPassword} className="srs-cta"
              style={{ marginTop: 22, width: "100%", justifyContent: "center" }} data-testid="reset-submit">
              {submitting ? (isEn ? "Saving…" : "Lagrer…") : (isEn ? "Save new password →" : "Lagre nytt passord →")}
            </button>
          </form>
        )}

        {done && (
          <div className="srs-card" style={{ width: "100%", maxWidth: 460, padding: 40, borderRadius: 24, textAlign: "center" }} data-testid="reset-success">
            <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-good)", marginBottom: 14 }}>✓ {isEn ? "PASSWORD CHANGED" : "PASSORD ENDRET"}</div>
            <h1 className="srs-display" style={{ fontSize: 28, fontWeight: 500, lineHeight: 1.15, margin: "0 0 14px 0" }}>
              {isEn ? "Done!" : "Ferdig!"}
            </h1>
            <p style={{ fontSize: 14, color: "var(--srs-ink-dim)", lineHeight: 1.55, margin: 0 }}>
              {isEn ? "Redirecting you to login…" : "Sender deg til innloggingen…"}
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

const labelCss = { display: "block", fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: "0.22em", fontWeight: 600, color: "var(--srs-ink-dim)", textTransform: "uppercase", marginBottom: 8 };
const inputCss = { width: "100%", background: "var(--srs-surface-2)", border: "1px solid var(--srs-line)", borderRadius: 10, padding: "12px 14px", fontSize: 14, color: "var(--srs-ink)", fontFamily: "inherit", outline: "none", boxSizing: "border-box" };
const errorCss = { marginTop: 16, padding: "10px 12px", background: "color-mix(in srgb, var(--srs-warn) 12%, transparent)", border: "1px solid var(--srs-warn)", borderRadius: 10, color: "var(--srs-warn)", fontSize: 12, lineHeight: 1.5 };
